import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
    useGetMigrationProfileQuery,
    useDeleteSourceConfigMutation,
    useCreateMigrationFromProfileMutation,
} from '../api';
import { useNotificationContext } from '../providers/NotificationProvider';
import { createCsvMigrationPreflightPayload } from '../lib/preflightUtils';

/**
 * Custom hook to handle CSV migration preflight logic
 * Replicates the backbone updateConfigsAndRunPreflight, syncSourceConfigs, and postMigration methods
 */
export const useCsvMigrationPreflight = () => {
    const [migrationProfileId, setMigrationProfileId] = useState(null);
    const [preflightPayload, setPreflightPayload] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const { notifySuccess, notifyError } = useNotificationContext();

    // API hooks
    const {
        data: migrationProfile,
        isLoading: isLoadingProfile,
        error: profileError,
    } = useGetMigrationProfileQuery(migrationProfileId, {
        skip: !migrationProfileId,
        refetchOnMountOrArgChange: true,
    });

    const [deleteSourceConfig] = useDeleteSourceConfigMutation();
    const [createMigrationFromProfile] = useCreateMigrationFromProfileMutation();

    /**
     * Start the preflight process for a migration profile
     * Replicates backbone updateConfigsAndRunPreflight method
     */
    const startPreflight = useCallback((profileId) => {
        setMigrationProfileId(profileId);
        setIsProcessing(false);
    }, []);

    /**
     * Generate preflight payload when migration profile is loaded
     * Replicates backbone preflight payload creation logic
     */
    const generatedPreflightPayload = useMemo(() => {
        if (migrationProfile) {
            return createCsvMigrationPreflightPayload(migrationProfile, false);
        }
        return null;
    }, [migrationProfile]);

    // Update the payload state when it changes
    useEffect(() => {
        setPreflightPayload(generatedPreflightPayload);
    }, [generatedPreflightPayload]);

    /**
     * Handle preflight completion and sync source configs
     * Replicates backbone syncSourceConfigs method
     */
    const handlePreflightComplete = useCallback(
        async (selectedSourceConfigs) => {
            if (!migrationProfile || !selectedSourceConfigs) {
                return;
            }

            setIsProcessing(true);

            try {
                // For CSV migrations, we typically start with all source configs selected
                // The backbone code has logic to remove unselected configs, but for CSV migrations
                // we usually want to process all configs that were uploaded

                // In the backbone version, this would call getSelectedSourceConfigs() and getUnselectedSelectedSourceConfigs()
                // For CSV migrations, we'll assume all configs should be processed
                const allSourceConfigIds = migrationProfile.sources.map((source) => source.id);
                const selectedConfigIds = selectedSourceConfigs.map((config) => config.resource_id || config.id);

                // Find configs to remove (those not selected)
                const configsToRemove = allSourceConfigIds.filter((id) => !selectedConfigIds.includes(id));

                if (configsToRemove.length > 0) {
                    // Remove unselected source configs
                    const removalPromises = configsToRemove.map((configId) =>
                        deleteSourceConfig({
                            migrationProfileId: migrationProfile.id,
                            sourceConfigId: configId,
                        }).unwrap(),
                    );

                    await Promise.all(removalPromises);
                }

                // Start migration
                await startMigration();
            } catch (error) {
                console.error('Error syncing source configs:', error);
                notifyError({ description: 'Error removing source configurations from migration profile.' });
                setIsProcessing(false);
            }
        },
        [migrationProfile, deleteSourceConfig, notifyError],
    );

    /**
     * Start the migration
     * Replicates backbone postMigration method
     */
    const startMigration = useCallback(async () => {
        if (!migrationProfile) {
            return;
        }

        try {
            await createMigrationFromProfile(migrationProfile.id).unwrap();

            // Set session storage message for migration list
            const msg = 'New migrations are being processed. They will appear in the list momentarily.';
            sessionStorage.setItem('preflight_referer_msg', msg);

            notifySuccess({
                description: 'Preflight validation was successful.',
                duration: 2,
            });

            // Navigate to migration list after a delay
            setTimeout(() => {
                window.reactNavigate('/migration_list');
            }, 2000);
        } catch (error) {
            console.error('Error starting migration:', error);
            notifyError({ description: 'Failed to start the Migration.' });
        } finally {
            setIsProcessing(false);
        }
    }, [migrationProfile, createMigrationFromProfile, notifySuccess, notifyError]);

    /**
     * Reset the hook state
     */
    const reset = useCallback(() => {
        setMigrationProfileId(null);
        setPreflightPayload(null);
        setIsProcessing(false);
    }, []);

    return {
        // State
        migrationProfile,
        preflightPayload,
        isLoadingProfile,
        isProcessing,
        profileError,

        // Actions
        startPreflight,
        handlePreflightComplete,
        reset,

        // Migration context for PreflightMonitor
        migrationContext: migrationProfile
            ? {
                  proceedOnSuccess: true,
                  dataOnlyMigration: false,
                  vdiMigration: false,
                  mandatoryTypes: ['migration_profile'],
                  alternateType: {
                      source_config: 'Target VM Config',
                      migration_profile: 'Migration Profile',
                  },
                  alternateName: {
                      source_config: {
                          path: 'target_config.properties.name',
                      },
                  },
              }
            : null,
    };
};
